import { inject } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import type { ResolveFn } from '@angular/router';
import { Router } from '@angular/router';
import { chain, cloneDeep } from 'lodash';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import type { PartialDeep } from 'type-fest';
import { v7 as uuidv7 } from 'uuid';
import { AuthStore } from '../../../../auth/common/stores/auth.store';
import type { FleetValidationByType } from '../../../../common/interfaces/validation/fleet-validation';
import { CacheService } from '../../../../common/services/cache.service';
import { PreferencesService } from '../../../../common/services/preferences.service';
import { ValidationService } from '../../../../common/services/validation.service';
import type { OrderPointForm } from '../../order-form/order-points-form/order-point-form/order-point-form.component';
import type { OrderFormResolverData } from '../../orders.routes';
import type { OrderDispatch } from '../enums/order-dispatch';
import { OrderPointCategory } from '../enums/order-point-category';
import { OrderTimeWindowType } from '../enums/order-time-window-type';
import { OrdersRouterStateKey } from '../enums/orders-router-state-key';
import { OrdersStorageKey } from '../enums/orders-storage-key';
import type { OrderForm } from '../helpers/order-form.helper';
import { OrderFormHelper } from '../helpers/order-form.helper';
import type { Order } from '../interfaces/order';

export const addOrderResolver: ResolveFn<
  Observable<
    OrderFormResolverData & {
      fleetValidation: FleetValidationByType;
    }
  >
> = () => {
  const authStore = inject(AuthStore);
  const router = inject(Router);
  const cacheService = inject(CacheService);
  const validationService = inject(ValidationService);
  const fb = inject(NonNullableFormBuilder);
  const preferencesService = inject(PreferencesService);
  const orderFormHelper = inject(OrderFormHelper);

  const state = router.currentNavigation()?.extras?.state;
  const template: Order | undefined = state?.[OrdersRouterStateKey.TEMPLATE];
  const defaultDispatch = orderFormHelper.getDefaultDispatch();

  return validationService.getFleetValidationByType().pipe(
    map((fleetValidation) => {
      if (template) {
        const form = fromTemplate(template, defaultDispatch);
        return {
          fleetValidation,
          form,
          initial: form,
          isDuplicate: true,
        };
      }

      const cache = cacheService.get<PartialDeep<OrderForm>>(
        OrdersStorageKey.ADD_ORDER_FORM,
        { keepOnNavigation: true },
      );

      if (cache?.points) {
        cache.points = chain(cache.points)
          .filter((point): point is OrderPointForm => !!point)
          .thru((points) => {
            const goodsUuidMap = new Map(
              points
                .filter(
                  (point) => point.category === OrderPointCategory.LOADING,
                )
                .flatMap((point) => point.goods ?? [])
                .map((good) => [good.uuid, uuidv7()]),
            );

            return points.map((point) => ({
              ...point,
              uuid: uuidv7(),
              ...('goods' in point &&
                point.goods && {
                  goods: point.goods.map((good) => ({
                    ...good,
                    uuid: goodsUuidMap.get(good.uuid) ?? uuidv7(),
                  })),
                }),
            }));
          })
          .value();
      }

      const form = OrderFormHelper.buildForm(
        fb,
        authStore,
        preferencesService,
        defaultDispatch,
      );
      const baseForm = form.getRawValue();
      const initial: PartialDeep<OrderForm> = {
        ...baseForm,
        points: baseForm.points?.length
          ? baseForm.points
          : [OrderFormHelper.buildLoadingPointForm(fb).getRawValue()],
      };

      return {
        fleetValidation,
        form: cache ?? initial,
        initial,
        isDuplicate: false,
      };
    }),
  );
};

const fromTemplate = (
  template: Order,
  defaultDispatch: OrderDispatch,
): OrderForm =>
  chain(template)
    .thru((dto) => OrderFormHelper.fromDto(cloneDeep(dto), defaultDispatch))
    .thru((form): OrderForm => ({
      dispatch: form.dispatch,
      basicInformation: {
        ...form.basicInformation,
        loadedSemiTrailer: null,
        isDropped: null,
        pickupPointUuid: null,
        dropPointUuid: null,
      },
      parameters: form.parameters,
      documents: [],
      note: { content: null },
      points: chain(form.points)
        .filter((point): point is OrderPointForm => !!point)
        .filter(
          (point) =>
            !('includeInOrder' in point) || point.includeInOrder !== false,
        )
        .filter((point) => point.category !== OrderPointCategory.PICKUP)
        .thru((points) => {
          const goodsUuidMap = new Map(
            points
              .filter((point) => point.category === OrderPointCategory.LOADING)
              .flatMap((point) => point.goods)
              .map((good) => [good.uuid, uuidv7()]),
          );

          return points.map((point) => {
            point.uuid = uuidv7();
            point.route = null;

            switch (point.category) {
              case OrderPointCategory.LOADING:
              case OrderPointCategory.UNLOADING:
                return {
                  ...point,
                  referenceNumber: null,
                  timeWindows: {
                    type: OrderTimeWindowType.INTERVAL,
                    windows: [{ date: null, time: null, timeRange: null }],
                  },
                  goods: point.goods.map((good) => ({
                    ...good,
                    uuid: goodsUuidMap.get(good.uuid)!,
                    referenceNumber: null,
                  })),
                };

              default:
                return point;
            }
          });
        })
        .value(),
      route: null,
    }))
    .value();
